import { useTranslation } from "react-i18next";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import { Building2, UserCircle, ShoppingBag, Layers, Settings2, Info } from "lucide-react";

const MODULE_INFO = {
  empresas: "Ao ATIVAR: o campo Empresa aparece como obrigatório no formulário de Negócios, fica disponível no Configurar Cards do Kanban e o menu Empresas aparece na navegação.\n\nAo INATIVAR: o campo Empresa some do formulário de Negócios, fica desabilitado no Configurar Cards e o menu Empresas é ocultado da navegação.",
  contatos: "Ao ATIVAR: o campo Contato aparece como obrigatório no formulário de Negócios, fica disponível no Configurar Cards do Kanban e o menu Contatos aparece na navegação.\n\nAo INATIVAR: o campo Contato some do formulário de Negócios, fica desabilitado no Configurar Cards e o menu Contatos é ocultado da navegação.",
  produtos: "Ao ATIVAR: o campo Produto aparece como obrigatório no formulário de Negócios, fica disponível no Configurar Cards do Kanban e o menu Produtos aparece na navegação.\n\nAo INATIVAR: o campo Produto some do formulário de Negócios, fica desabilitado no Configurar Cards e o menu Produtos é ocultado da navegação.",
};
import { useAppearance } from "../contexts/AppearanceContext";
import { useModules } from "../contexts/ModulesContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  empresas: Building2,
  contatos: UserCircle,
  produtos: ShoppingBag,
};

const MODULE_COLORS = {
  empresas: "#10B981",
  contatos: "#F43F5E",
  produtos: "#8B5CF6",
};

export default function ModulosPage() {
  const { t } = useTranslation();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const appearance = useAppearance();
  const { refreshModules } = useModules();
  const tc500 = appearance.colors?.["500"] || "#06B6D4";
  const tc50  = appearance.colors?.["50"]  || "#ECFEFF";

  const fetchModules = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/modules`, { withCredentials: true });
      setModules(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchModules(); }, [fetchModules]);

  const handleToggle = async (moduleName, newEnabled) => {
    setSaving(prev => ({ ...prev, [moduleName]: true }));
    try {
      await axios.put(`${API}/modules/${moduleName}`, { enabled: newEnabled }, { withCredentials: true });
      setModules(prev => prev.map(m => m.module === moduleName ? { ...m, enabled: newEnabled } : m));
      refreshModules(); // Update sidebar immediately
    } catch (err) { console.error(err); }
    finally { setSaving(prev => ({ ...prev, [moduleName]: false })); }
  };

  return (
    <div data-testid="modulos-page" className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: tc50 }}>
          <Layers className="w-5 h-5" style={{ color: tc500 }} strokeWidth={1.5} />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-900 tracking-tight">{t("configuracoes.modulos.title")}</h1>
          <p className="text-slate-500 mt-0.5">{t("configuracoes.modulos.subtitle")}</p>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map(i => <div key={i} className="aspect-square bg-white rounded-xl border border-slate-200 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {modules.map(mod => {
            const Icon = MODULE_ICONS[mod.module] || Layers;
            const color = MODULE_COLORS[mod.module] || tc500;
            const isEnabled = mod.enabled;
            return (
              <div key={mod.module}
                className={`relative flex flex-col rounded-xl border-2 bg-white p-4 transition-all ${isEnabled ? "border-slate-200 shadow-sm" : "border-dashed border-slate-200 opacity-70"}`}>

                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5" style={{ color: isEnabled ? tc500 : "#cbd5e1" }} strokeWidth={1.5} />
                    <h3 className="text-sm font-bold text-slate-900">{mod.name}</h3>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" className="cursor-help">
                        <Info className="w-3.5 h-3.5 text-slate-300 hover:text-slate-500 transition-colors" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-xs text-xs whitespace-pre-line">
                      {MODULE_INFO[mod.module]}
                    </TooltipContent>
                  </Tooltip>
                </div>

                {/* Description */}
                <p className="text-xs text-slate-500 leading-relaxed mb-4 flex-1">{mod.description}</p>

                {/* Footer: toggle + config btn */}
                <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={isEnabled}
                      disabled={!!saving[mod.module]}
                      onCheckedChange={(v) => handleToggle(mod.module, v)}
                    />
                    <span className="text-xs font-medium text-slate-500">
                      {isEnabled ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="outline" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600">
                        <Settings2 className="w-3 h-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t("ui.config_em_breve")}</TooltipContent>
                  </Tooltip>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
